import { useMutation } from "@apollo/client/react";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Button as MuiButton,
  TextField,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { SaveAccountV2Mutation, SaveAccountV2Document, SaveAccountV2MutationVariables } from "../../../graphql/types";

const Button = styled(MuiButton)(({ theme }) => {
  return {
    height: "50px",
    width: "100%",
    marginTop: theme.spacing(2),
    fontSize: "14px",
  };
});

const TextFieldStyled = styled(TextField)({
  width: "100%",
});

type DefaultValuesFormType = {
  accountName: string;
  urlName: string;
  loginName: string | null;
  loginEmail: string | null;
  loginPassword: string | null;
  roles: Array<string> | null;
  addLogin: boolean;
};

const CreateAccount = ({
  showModal,
  setShowModal,
}: {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}) => {
  const [saveAccountV2, saveAccountState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {
      awaitRefetchQueries: false,
      refetchQueries: () => ["accountsV2"],
      onCompleted: (result: SaveAccountV2Mutation) => {
        if (result.saveAccountV2.__typename === "ResultOk") {
          toast.success("Account Saved");
          reset();
          setShowModal(false);
        } else if (result.saveAccountV2.__typename === "SaveAccountErrors") {
          result.saveAccountV2.errors?.forEach((error) => {
            if (error?.__typename === "ConflictingNameError") {
              setError("accountName", {
                message: error.message,
              });
            } else if (error?.__typename === "ConflictingUrlNameError") {
              setError("urlName", {
                message: error.message,
              });
            }
          });
        }
      },
      onError: () => {
        toast.error("Error when saving account");
      },
    }
  );
  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    getValues,
    setError,
  } = useForm<DefaultValuesFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      accountName: "",
      urlName: "",
    },
  });

  const onSubmit = () => {
    const variables = {
      name: getValues("accountName").trim(),
      newUrlName: getValues("urlName").trim(),
    };
    saveAccountV2({
      variables: variables,
    });
  };

  return (
    <Dialog
      open={showModal}
      onClose={() => {
        setShowModal(false);
        reset();
      }}
      data-testid="add-account-modal"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle variant="heading.four" sx={{ mb: 1 }}>
          Create a new account
        </DialogTitle>
        <DialogContent>
          <Controller
            name="accountName"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextFieldStyled
                {...field}
                margin="normal"
                error={Boolean(fieldState.error)}
                required
                id="accountName"
                label="Account Name"
                type="text"
                autoFocus
                data-testid="account-name"
                helperText={fieldState.error?.message}
                autoComplete="off"
              />
            )}
          />
          <Controller
            name="urlName"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextFieldStyled
                {...field}
                margin="normal"
                error={Boolean(fieldState.error)}
                required
                id="urlName"
                label="URL Name"
                type="text"
                data-testid="account-url-name"
                helperText={fieldState.error?.message}
                autoComplete="off"
              />
            )}
          />
          <Button type="submit" disabled={!isValid || saveAccountState.loading} data-testid="save-account-button">
            {saveAccountState.loading ? (
              <CircularProgress size="2rem" sx={(theme) => ({ color: theme.palette.text10.main })} />
            ) : (
              "Create"
            )}
          </Button>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export { CreateAccount };
